import classNames from "classnames";
import React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";

import { ButtonContainer } from "components/button-container/ButtonContainer";
import Button from "components/button/Button";
import ErrorMessage from "components/error-message/ErrorMessage";
import IconButton from "components/icon-button/IconButton";
import LeftArrow from "components/icons/LeftArrow";
import { subpageContext } from "components/layout/ApplicationLayout";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import BarSteps from "components/products/bar-home-page/BarSteps";
import BdeSteps from "components/products/bde-home-page/BdeSteps";
import BmdeSteps from "components/products/bmde-home-page/BmdeSteps";
import BmpOpSteps from "components/products/bmpop-home-page/BmpOpSteps";
import { BmpSetup } from "components/products/BmpSetup";
import BmweSteps from "components/products/bmwe-home-page/BmweSteps";
import style from "components/products/getting-started.scss";
import ProgressBar from "components/products/ProgressBar";
import Heading from "components/typography/heading/Heading";
import {
    AUTH_REPORT_VIEW,
    AUTH_WORKFLOW_DEFAULT,
    AUTH_WORKFLOW_TEMPLATE_SAVE,
    AUTH_WORKFLOW_VIEW,
} from "domain/authority";
import { LicenseData } from "domain/licenses";
import {
    BAR_ONBOARDING_STEPS,
    BarOnboardingSteps,
    BDE_ONBOARDING_STEPS,
    BdeOnboardingSteps,
    BMDE_ONBOARDING_STEPS,
    BmdeOnboardingSteps,
    BMPOP_ONBOARDINGS_STEP,
    BmpOpOnboardingSteps,
    BMWE_ONBOARDING_STEPS,
    BmweOnboardingSteps,
    displayProgressBarProgress,
    getInitialProgressPercentage,
    getStepIndexForBar,
    getStepIndexForBde,
    getStepIndexForBmde,
    getStepIndexForBmpOp,
    getStepIndexForBmwe,
    isOnLastStep,
    mapCompletedStepToBarOnboardingStep,
    mapCompletedStepToBdeOnboardingStep,
    mapCompletedStepToBmdeOnboardingStep,
    mapCompletedStepToBmpOpOnboardingStep,
    mapCompletedStepToBmweOnboardingStep,
    OnboardingProductType,
} from "domain/onboardings";
import { OnboardingImage, SoftwarePackage, Versions } from "domain/softwarePackages";
import { FeatureLicenseType } from "domain/users";
import { ConnectedWorkflows } from "domain/workflows";
import { TemplateTableData } from "domain/workflowTemplates";
import { Onboarding, onboardingsService } from "services/onboardings/OnboardingsService";
import { isInTenantAccess } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { templateService } from "services/workflows/TemplateService";
import { EditorGeneration, Profile } from "services/workflows/WorkflowService";
import buttons from "styles/buttons.scss";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface Props {
    product: OnboardingProductType;
    visible: boolean;
    title: string;
    extraHeaderText?: string;
    description: string | React.ReactNode;
    footerVisibility?: boolean;
    buttons?: React.ReactNode;
    children?: React.ReactNode;
    primaryButtonRequired?: boolean;
    handleRefresh?: (value: boolean) => void;
    fetchedSoftwarePackages?: SoftwarePackage[];
    licenses: Versions[];
    fetchedLicenses: LicenseData[];
    saveBmdeOnboarded?: (onboarded: boolean) => void;
    saveBdeOnboarded?: (onboarded: boolean) => void;
    saveBmpOpOnboarded?: (onboarded: boolean) => void;
    saveBarOnboarded?: (onboarded: boolean) => void;
    saveBmweOnboarded?: (onboarded: boolean) => void;
    showBmpSetup: boolean;
    saveLastCompletedStep: (step: string) => void;
    fetchedOnboardingImages: OnboardingImage[];
}

export const GettingStartedLayout = (props: Props) => {
    const { t } = useTranslation();
    const context = React.useContext(subpageContext);

    const theme = useTheme();

    const hasFeatureLicense = () => {
        return userSessionService.hasFeatureLicense("FEATURE_REPORTING");
    };
    const canViewReport = userSessionService.userHasAnyAuthority([AUTH_REPORT_VIEW]) && hasFeatureLicense();
    const canViewWorkflows = userSessionService.userHasAllAuthorities([
        AUTH_WORKFLOW_VIEW,
        AUTH_WORKFLOW_DEFAULT,
        AUTH_WORKFLOW_TEMPLATE_SAVE,
    ]);
    const checkIbr = (product: string) => {
        const featureLicense: FeatureLicenseType =
            product === OnboardingProductType.BDE ? "FEATURE_WORKFLOW_BDE" : "FEATURE_WORKFLOW_BMDE";
        return userSessionService.hasFeatureLicense(featureLicense) && canViewWorkflows;
    };
    const [stepInProgressIndex, setStepInProgressIndex] = React.useState(-1);
    const [lastCompletedStep, setLastCompletedStep] = React.useState<string>("");
    const [showBmpSetup, setShowBmpSetup] = React.useState<boolean>(props.showBmpSetup);
    const [feedbackSubmitted, setFeedbackSubmitted] = React.useState<boolean>(false);
    const bmdeStep = React.useCallback((): BmdeOnboardingSteps | undefined => {
        if (props.product === OnboardingProductType.BMDE) {
            return mapCompletedStepToBmdeOnboardingStep(lastCompletedStep, checkIbr(OnboardingProductType.BMDE));
        }
    }, [lastCompletedStep, stepInProgressIndex]);
    const bdeStep = React.useCallback((): BdeOnboardingSteps | undefined => {
        if (props.product === OnboardingProductType.BDE) {
            return mapCompletedStepToBdeOnboardingStep(lastCompletedStep, checkIbr(OnboardingProductType.BDE));
        }
    }, [lastCompletedStep, stepInProgressIndex]);

    const bmpOpStep = React.useCallback((): BmpOpOnboardingSteps | undefined => {
        if (props.product === OnboardingProductType.BMPOP) {
            return mapCompletedStepToBmpOpOnboardingStep(lastCompletedStep);
        }
    }, [lastCompletedStep, stepInProgressIndex]);

    const barStep = React.useCallback((): BarOnboardingSteps | undefined => {
        if (props.product === OnboardingProductType.BAR) {
            return mapCompletedStepToBarOnboardingStep(lastCompletedStep);
        }
    }, [lastCompletedStep, stepInProgressIndex]);

    const bmweStep = React.useCallback((): BmweOnboardingSteps | undefined => {
        if (props.product === OnboardingProductType.BMWE) {
            return mapCompletedStepToBmweOnboardingStep(lastCompletedStep, checkIbr(OnboardingProductType.BMWE));
        }
    }, [lastCompletedStep, stepInProgressIndex]);

    const lastCompletedBmdeStep = !bmdeStep()
        ? -1
        : getStepIndexForBmde(bmdeStep(), checkIbr(OnboardingProductType.BMDE));
    const lastCompletedBdeStep = !bdeStep ? -1 : getStepIndexForBde(bdeStep(), checkIbr(OnboardingProductType.BDE));
    const [lastCompletedStepIndexBmde, setLastCompletedStepIndexBmde] = React.useState<number>(-1);
    const [lastCompletedStepIndexBde, setLastCompletedStepIndexBde] = React.useState<number>(-1);

    const lastCompletedBmpOpStep = !bmpOpStep ? -1 : getStepIndexForBmpOp(bmpOpStep());
    const [lastCompletedStepIndexBmpOp, setLastCompletedStepIndexBmpOp] = React.useState<number>(-1);

    const lastCompletedBarStep = !barStep ? -1 : getStepIndexForBar(barStep());
    const [lastCompletedStepIndexBar, setLastCompletedStepIndexBar] = React.useState<number>(-1);

    const lastCompletedBmweStep = !bmweStep
        ? -1
        : getStepIndexForBmwe(bmweStep(), checkIbr(OnboardingProductType.BMWE));
    const [lastCompletedStepIndexBmwe, setLastCompletedStepIndexBmwe] = React.useState<number>(-1);

    React.useEffect(() => {
        setLastCompletedStepIndexBmde(lastCompletedBmdeStep);
        setLastCompletedStepIndexBde(lastCompletedBdeStep);
        setLastCompletedStepIndexBmpOp(lastCompletedBmpOpStep);
        setLastCompletedStepIndexBar(lastCompletedBarStep);
        setLastCompletedStepIndexBmwe(lastCompletedBmweStep);
    }, [
        lastCompletedBmdeStep,
        lastCompletedBdeStep,
        lastCompletedBmpOpStep,
        lastCompletedBarStep,
        lastCompletedBmweStep,
    ]);

    const [progressValueBmpOp, setProgressValueBmpOp] = React.useState<number>(
        getInitialProgressPercentage(lastCompletedStepIndexBmpOp, false, canViewReport, BMPOP_ONBOARDINGS_STEP)
    );

    const [progressValueBmde, setProgressValueBmde] = React.useState<number>(
        getInitialProgressPercentage(
            lastCompletedStepIndexBmde,
            checkIbr(OnboardingProductType.BMDE),
            canViewReport,
            BMDE_ONBOARDING_STEPS
        )
    );
    const [progressValueBde, setProgressValueBde] = React.useState<number>(
        getInitialProgressPercentage(
            lastCompletedStepIndexBde,
            checkIbr(OnboardingProductType.BDE),
            canViewReport,
            BDE_ONBOARDING_STEPS
        )
    );

    const [progressValueBar, setProgressValueBar] = React.useState<number>(
        getInitialProgressPercentage(lastCompletedStepIndexBar, false, false, BAR_ONBOARDING_STEPS)
    );

    const [progressValueBmwe, setProgressValueBmwe] = React.useState<number>(
        getInitialProgressPercentage(
            lastCompletedStepIndexBmwe,
            checkIbr(OnboardingProductType.BMWE),
            canViewReport,
            BMWE_ONBOARDING_STEPS
        )
    );

    React.useEffect(() => {
        const onLastStep = lastCompletedStep ? isOnLastStep(lastCompletedStep, props.product) : false;
        setShowBmpSetup(onLastStep);
    }, [lastCompletedStep, props.product]);

    React.useEffect(() => {
        if (props.product === OnboardingProductType.BMDE) {
            if (lastCompletedBmdeStep === lastCompletedStepIndexBmde) {
                setProgressValueBmde(
                    getInitialProgressPercentage(
                        lastCompletedStepIndexBmde,
                        checkIbr(OnboardingProductType.BMDE),
                        canViewReport,
                        BMDE_ONBOARDING_STEPS
                    )
                );
            } else {
                setProgressValueBmde(
                    displayProgressBarProgress(
                        lastCompletedStepIndexBmde,
                        progressValueBmde,
                        checkIbr(OnboardingProductType.BMDE)
                    )
                );
            }
        }

        if (props.product === OnboardingProductType.BDE) {
            if (lastCompletedBdeStep === lastCompletedStepIndexBde) {
                setProgressValueBde(
                    getInitialProgressPercentage(
                        lastCompletedStepIndexBde,
                        checkIbr(OnboardingProductType.BDE),
                        canViewReport,
                        BDE_ONBOARDING_STEPS
                    )
                );
            } else {
                setProgressValueBde(
                    displayProgressBarProgress(
                        lastCompletedStepIndexBde,
                        progressValueBde,
                        checkIbr(OnboardingProductType.BDE)
                    )
                );
            }
        }

        if (props.product === OnboardingProductType.BMPOP) {
            if (lastCompletedBmpOpStep === lastCompletedStepIndexBmpOp) {
                setProgressValueBmpOp(
                    getInitialProgressPercentage(lastCompletedStepIndexBmpOp, false, false, BMPOP_ONBOARDINGS_STEP)
                );
            } else {
                setProgressValueBmpOp(
                    displayProgressBarProgress(lastCompletedStepIndexBmpOp, progressValueBmpOp, false)
                );
            }
        }

        if (props.product === OnboardingProductType.BAR) {
            if (lastCompletedBarStep === lastCompletedStepIndexBar) {
                setProgressValueBar(
                    getInitialProgressPercentage(lastCompletedStepIndexBar, false, false, BAR_ONBOARDING_STEPS)
                );
            } else {
                setProgressValueBar(displayProgressBarProgress(lastCompletedStepIndexBar, progressValueBar, false));
            }
        }

        if (props.product === OnboardingProductType.BMWE) {
            if (lastCompletedBmweStep === lastCompletedStepIndexBmwe) {
                setProgressValueBmwe(
                    getInitialProgressPercentage(
                        lastCompletedStepIndexBmwe,
                        checkIbr(OnboardingProductType.BMWE),
                        canViewReport,
                        BMWE_ONBOARDING_STEPS
                    )
                );
            } else {
                setProgressValueBmwe(
                    displayProgressBarProgress(
                        lastCompletedStepIndexBmwe,
                        progressValueBmwe,
                        checkIbr(OnboardingProductType.BMWE)
                    )
                );
            }
        }
    }, [
        lastCompletedStepIndexBmde,
        lastCompletedStepIndexBde,
        lastCompletedStepIndexBmpOp,
        lastCompletedStepIndexBar,
        lastCompletedStepIndexBmwe,
    ]);

    const connectedWorkflows: ConnectedWorkflows = {
        collectWorkflows: [],
        gradingPanelWorkflows: [],
        remoteWorkflows: 0,
        runDiagnoseWorkflows: [],
        runProcessFlowWorkflows: [],
        subWorkflows: [],
        forEachWorkflows: [],
        advancedGradingWorkflows: [],
        totalConnectedWorkflows: 0,
        parentWorkflows: [],
    };

    const editorGeneration: EditorGeneration = "V1";
    const profile: Profile = Profile.ALL;
    const defaultTemplateTable: TemplateTableData = {
        connectedWorkflows: connectedWorkflows,
        coreActivities: [],
        created: "",
        creator: "",
        editorGeneration: editorGeneration,
        editorVersion: "",
        modified: "",
        onboarding: false,
        own: false,
        processors: [],
        profile: profile,
        published: false,
        saved: false,
        templateType: "",
        update: false,
        usageCount: 0,
        useCases: [],
        secured: false,
        userName: "",
        userUuid: "",
        description: "",
        name: "",
        uuid: "",
    };
    const abortController = new AbortController();
    const [isLoading, setIsLoading] = React.useState(true);
    const [requestFailureMessage, setRequestFailureMessage] = React.useState<string>("");
    const [defaultWorkflowTemplate, setDefaultWorkflowTemplate] =
        React.useState<TemplateTableData>(defaultTemplateTable);
    const [showError, setShowError] = React.useState(false);

    const handleCloseClick = () => {
        context?.setSubpage({
            content: undefined,
        });
    };

    function saveOnboardingStep(onboarding: Onboarding, abortController: AbortController) {
        if (onboarding.product === OnboardingProductType.BDE) {
            setStepInProgressIndex(
                getStepIndexForBde(
                    mapCompletedStepToBdeOnboardingStep(onboarding.step, checkIbr(props.product)),
                    checkIbr(props.product)
                )
            );
        } else if (onboarding.product === OnboardingProductType.BMDE) {
            setStepInProgressIndex(
                getStepIndexForBmde(
                    mapCompletedStepToBmdeOnboardingStep(onboarding.step, checkIbr(props.product)),
                    checkIbr(props.product)
                )
            );
        } else if (onboarding.product === OnboardingProductType.BMPOP) {
            setStepInProgressIndex(getStepIndexForBmpOp(mapCompletedStepToBmpOpOnboardingStep(onboarding.step)));
        } else if (onboarding.product === OnboardingProductType.BAR) {
            setStepInProgressIndex(getStepIndexForBar(mapCompletedStepToBarOnboardingStep(onboarding.step)));
        } else if (onboarding.product === OnboardingProductType.BMWE) {
            setStepInProgressIndex(
                getStepIndexForBmwe(
                    mapCompletedStepToBmweOnboardingStep(onboarding.step, checkIbr(props.product)),
                    checkIbr(props.product)
                )
            );
        }
        onboardingsService
            .saveOnboardingStep(onboarding, abortController)
            .then(() => {
                setStepInProgressIndex(-1);
                setLastCompletedStep(onboarding.step);
                props.saveLastCompletedStep(onboarding.step);
                const onLastStep = isOnLastStep(onboarding.step, onboarding.product);
                if (onLastStep) {
                    if (onboarding.product === OnboardingProductType.BMDE && props.saveBmdeOnboarded) {
                        props.saveBmdeOnboarded(true);
                    }

                    if (onboarding.product === OnboardingProductType.BDE && props.saveBdeOnboarded) {
                        props.saveBdeOnboarded(true);
                    }
                    if (onboarding.product === OnboardingProductType.BMPOP && props.saveBmpOpOnboarded) {
                        props.saveBmpOpOnboarded(true);
                    }
                    if (onboarding.product === OnboardingProductType.BAR && props.saveBarOnboarded) {
                        props.saveBarOnboarded(true);
                    }
                    if (onboarding.product === OnboardingProductType.BMWE && props.saveBmweOnboarded) {
                        props.saveBmweOnboarded(true);
                    }
                }
                setShowBmpSetup(onLastStep);
            })
            .catch(() => {
                setShowError(true);
                setRequestFailureMessage(t("Onboarding.bmde.errorMessages.onboardingStepNotSaved"));
            });
    }

    const fetchOnboardings = (abortController: AbortController) => {
        onboardingsService
            .fetchOnboardings(props.product, abortController)
            .then((onboarding) => {
                if (onboarding.length > 0) {
                    setLastCompletedStep(onboarding[0].step);
                    setFeedbackSubmitted(onboarding[0].feedbackSubmitted || false);
                } else {
                    setLastCompletedStep("");
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    React.useEffect(() => {
        !isInTenantAccess() ? fetchOnboardings(abortController) : setIsLoading(false);
    }, []);

    const deduceSearchName = (): string => {
        if (props.product === OnboardingProductType.BMWE) {
            return "blancco mobile workspace edition";
        } else {
            return props.product;
        }
    };
    const fetchData = (abortController: AbortController) => {
        templateService
            .fetchWorkflowTemplates(
                deduceSearchName(),
                "",
                "",
                "",
                [],
                [],
                [],
                true,
                "",
                "",
                "",
                false,
                "",
                true,
                abortController
            )
            .then((data) => {
                if (data?.workflowTemplatesTableData.length > 0) {
                    setDefaultWorkflowTemplate(data.workflowTemplatesTableData[0]);
                    setRequestFailureMessage("");
                } else {
                    setDefaultWorkflowTemplate(defaultTemplateTable);
                }
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setRequestFailureMessage(t("workflowTemplatesManager.template.requestFailed"));
                    setShowError(true);
                }
            });
    };
    React.useEffect(() => {
        if (checkIbr(props.product)) {
            fetchData(abortController);
        }
    }, [props.fetchedLicenses]);

    const hideErrorModal = () => {
        setShowError(false);
    };

    function setUpBmp() {
        return (
            <BmpSetup
                product={props.product}
                showFeedback={!feedbackSubmitted}
                step={lastCompletedStep}
                showSteps={showBmpSetup}
            />
        );
    }

    return (
        <div className={style.pageLayout}>
            {isLoading ? (
                <LoadingIndicator />
            ) : (
                <div>
                    <div className={classNames(style.layoutPadding, style.pageContainer, style.page)}>
                        <div className={style.pageHeader}>
                            {props.extraHeaderText ? (
                                <div className={style.layoutTopText}>{props.extraHeaderText + " /"}</div>
                            ) : null}
                            <div className={style.headingLayout}>
                                <IconButton
                                    onClick={handleCloseClick}
                                    testId={testIds.common.subpageView.backToPrimaryViewButton}
                                >
                                    <LeftArrow color={theme.iconFillColor} />
                                </IconButton>
                                <Heading className={style.headingText} tag="h1" variant="H1">
                                    {props.title}
                                </Heading>
                            </div>
                            <div className={style.layoutTopText}>{props.description}</div>
                        </div>
                        <ProgressBar
                            progressValue={
                                props.product === OnboardingProductType.BMDE
                                    ? progressValueBmde
                                    : props.product === OnboardingProductType.BMPOP
                                      ? progressValueBmpOp
                                      : props.product === OnboardingProductType.BAR
                                        ? progressValueBar
                                        : props.product === OnboardingProductType.BMWE
                                          ? progressValueBmwe
                                          : progressValueBde
                            }
                        />
                        <div>
                            {props.product === OnboardingProductType.BDE ? (
                                <BdeSteps
                                    lastCompletedStepIndex={lastCompletedStepIndexBde}
                                    fetchedSoftwarePackages={props.fetchedSoftwarePackages}
                                    defaultWorkflowTemplate={defaultWorkflowTemplate}
                                    requestFailureMessage={requestFailureMessage}
                                    fetchedLicenses={props.fetchedLicenses}
                                    canViewReport={canViewReport}
                                    licenses={props.licenses}
                                    saveOnboardingStep={(onboarding: Onboarding) =>
                                        saveOnboardingStep(onboarding, abortController)
                                    }
                                    hasIbr={checkIbr(OnboardingProductType.BDE)}
                                    stepInProgressIndex={stepInProgressIndex}
                                    saveStepInProgressIndex={setStepInProgressIndex}
                                    fetchedOnboardingImages={props.fetchedOnboardingImages}
                                />
                            ) : props.product === OnboardingProductType.BMDE ? (
                                <BmdeSteps
                                    lastCompletedStepIndex={lastCompletedStepIndexBmde}
                                    fetchedSoftwarePackages={props.fetchedSoftwarePackages}
                                    licenses={props.licenses}
                                    defaultWorkflowTemplate={defaultWorkflowTemplate}
                                    requestFailureMessage={requestFailureMessage}
                                    fetchedLicenses={props.fetchedLicenses}
                                    canViewReport={canViewReport}
                                    canViewWorkflows={checkIbr(OnboardingProductType.BMDE)}
                                    saveOnboardingStep={(onboarding: Onboarding) =>
                                        saveOnboardingStep(onboarding, abortController)
                                    }
                                    stepInProgressIndex={stepInProgressIndex}
                                    saveStepInProgressIndex={setStepInProgressIndex}
                                    fetchedOnboardingImages={props.fetchedOnboardingImages}
                                />
                            ) : props.product === OnboardingProductType.BMPOP ? (
                                <BmpOpSteps
                                    lastCompletedStepIndex={lastCompletedStepIndexBmpOp}
                                    images={props.fetchedOnboardingImages}
                                    licenses={props.licenses}
                                    fetchedLicenses={props.fetchedLicenses}
                                    saveOnboardingStep={(onboarding: Onboarding) =>
                                        saveOnboardingStep(onboarding, abortController)
                                    }
                                    stepInProgressIndex={stepInProgressIndex}
                                    saveStepInProgressIndex={setStepInProgressIndex}
                                />
                            ) : props.product === OnboardingProductType.BAR ? (
                                <BarSteps
                                    lastCompletedStepIndex={lastCompletedStepIndexBar}
                                    licenses={props.licenses}
                                    fetchedLicenses={props.fetchedLicenses}
                                    fetchedSoftwarePackages={props.fetchedSoftwarePackages}
                                    saveOnboardingStep={(onboarding: Onboarding) =>
                                        saveOnboardingStep(onboarding, abortController)
                                    }
                                    stepInProgressIndex={stepInProgressIndex}
                                    saveStepInProgressIndex={setStepInProgressIndex}
                                    fetchedOnboardingImages={props.fetchedOnboardingImages}
                                />
                            ) : props.product === OnboardingProductType.BMWE ? (
                                <BmweSteps
                                    lastCompletedStepIndex={lastCompletedStepIndexBmwe}
                                    fetchedSoftwarePackages={props.fetchedSoftwarePackages}
                                    defaultWorkflowTemplate={defaultWorkflowTemplate}
                                    requestFailureMessage={requestFailureMessage}
                                    fetchedLicenses={props.fetchedLicenses}
                                    canViewReport={canViewReport}
                                    licenses={props.licenses}
                                    saveOnboardingStep={(onboarding: Onboarding) =>
                                        saveOnboardingStep(onboarding, abortController)
                                    }
                                    hasIbr={checkIbr(OnboardingProductType.BMWE)}
                                    stepInProgressIndex={stepInProgressIndex}
                                    saveStepInProgressIndex={setStepInProgressIndex}
                                    fetchedOnboardingImages={props.fetchedOnboardingImages}
                                />
                            ) : null}
                            {setUpBmp()}
                        </div>
                        <div className={style.pageContent}>
                            <ErrorBoundary FallbackComponent={ErrorMessage}>{props.children}</ErrorBoundary>
                        </div>
                    </div>
                    {props.footerVisibility ? (
                        <div className={classNames(style.layoutPadding, style.pageFooter)}>
                            <ButtonContainer alignment="LEFT" noSpacingTop={true}>
                                {props.buttons}
                                <Button
                                    variant={props.primaryButtonRequired ? "PRIMARY" : "SECONDARY"}
                                    type={"button"}
                                    onClick={handleCloseClick}
                                    testId={testIds.common.subpageView.cancelButton}
                                >
                                    {props.primaryButtonRequired ? t("Common.ok") : t("Common.cancel")}
                                </Button>
                            </ButtonContainer>
                        </div>
                    ) : null}

                    <Modal isOpen={showError} hideModal={hideErrorModal} modalTitle={requestFailureMessage}>
                        <>
                            <div className={style.errorMessageContainer}>
                                {t("Onboarding.bmde.errorMessages.genericError")}
                            </div>
                            <div className={style.errorButtonContainer}>
                                <button
                                    className={classNames(
                                        buttons.primaryButton,
                                        buttons.medium,
                                        buttons.okButton,
                                        buttons.buttonWithoutIcon
                                    )}
                                    onClick={() => {
                                        setShowError(false);
                                    }}
                                >
                                    {t("Common.ok")}
                                </button>
                            </div>
                        </>
                    </Modal>
                </div>
            )}
        </div>
    );
};
